// Page Situations de Travail - Information sur les risques prioritaires par métier
// Page informative entre sélection métiers et Q1
// Layout responsive : 2 colonnes ≥1440px (image sticky + tableau), vertical en dessous

import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { trackEvent } from '../lib/analytics'
import { Info, Expand } from 'lucide-react'
import { Card, CardContent } from '../components/ui'
import { Badge } from '../components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'
import { Dialog, DialogTrigger, DialogContent, DialogTitle } from '../components/ui/dialog'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'

// Type pour les situations de travail
interface SituationTravail {
  metierId: string
  ordreAffichage: number
  situationTravail: string
  risquesAssocies: string[]
  metierAssocie: string
}

// Type pour les données groupées par situation
interface SituationGroupee {
  situation: string
  ordreAffichage: number
  risques: string[]
}

// Type pour les données JSON
interface SituationsData {
  data: SituationTravail[]
}

// Sous-composant : contenu d'un métier
// ≥1440px : image sticky à gauche (1/3) + tableau à droite (2/3)
// <1440px  : miniature cliquable (h-40 crop) + Dialog plein écran + tableau en dessous
function MetierSituations({
  metier,
  situations,
}: {
  metier: { code: string; nom: string; ordre: number }
  situations: SituationGroupee[]
}) {
  const [imageOpen, setImageOpen] = useState(false)
  const imageSrc = `/images/metiers/illustration-${metier.code.toLowerCase()}.png`

  return (
    <div className="min-[1440px]:flex min-[1440px]:gap-6 mt-4">
      {/* ========== IMAGE — colonne gauche sticky ≥1440px ========== */}
      <div className="hidden min-[1440px]:block min-[1440px]:w-1/3 min-[1440px]:flex-shrink-0">
        <div className="sticky top-4 rounded-lg overflow-hidden border border-border">
          <img
            src={imageSrc}
            alt={`Risques identifiés — ${metier.nom}`}
            loading="lazy"
            className="w-full h-auto"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        </div>
      </div>

      {/* ========== IMAGE — miniature cliquable <1440px ========== */}
      <div className="min-[1440px]:hidden">
        <Dialog open={imageOpen} onOpenChange={setImageOpen}>
          <DialogTrigger asChild>
            <button className="w-full rounded-lg overflow-hidden border border-border relative group cursor-zoom-in">
              <img
                src={imageSrc}
                alt={`Risques identifiés — ${metier.nom}`}
                loading="lazy"
                className="w-full h-40 object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <Expand className="size-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" />
              </div>
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl p-2">
            <DialogTitle className="sr-only">Infographie {metier.nom}</DialogTitle>
            <img
              src={imageSrc}
              alt={`Risques identifiés — ${metier.nom}`}
              className="w-full h-auto rounded"
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* ========== TABLEAU — colonne droite ≥1440px, pleine largeur sinon ========== */}
      <div className="min-[1440px]:flex-1 min-[1440px]:min-w-0">
        {/* Compteur */}
        <p className="text-sm text-muted-foreground mt-4 min-[1440px]:mt-0 mb-2">
          {situations.length} situation{situations.length > 1 ? 's' : ''} identifiée{situations.length > 1 ? 's' : ''}
        </p>

        {/* Tableau compact dans une seule Card */}
        {situations.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              {/* En-tête */}
              <div className="flex items-center px-4 py-2 border-b border-border bg-muted/50">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider w-8">#</span>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Situation de travail et risques associés</span>
              </div>

              {/* Lignes */}
              <div className="divide-y divide-border">
                {situations.map((situation, index) => (
                  <div key={index} className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-muted-foreground w-8 flex-shrink-0 pt-0.5">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm leading-snug">
                          {situation.situation}
                        </p>
                        {situation.risques.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {situation.risques.map((risque, rIndex) => (
                              <Badge key={rIndex} variant="secondary" className="text-xs font-normal border border-primary/20 bg-primary/5 text-primary">
                                {risque}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Aucune situation de travail prioritaire identifiée pour ce métier.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>{situations.length} situation{situations.length > 1 ? 's' : ''} au total</span>
          <span>Source : Données OPPBTP</span>
        </div>
      </div>
    </div>
  )
}

export default function SituationsTravailPage() {
  const navigate = useNavigate()
  const { id: diagnosticId } = useParams<{ id: string }>()

  // Charger les données du diagnostic (depuis state ou API)
  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [situationsData, setSituationsData] = useState<SituationTravail[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Charger la progression du diagnostic pour le stepper
  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  // Étapes de progression
  const steps = useDiagnosticSteps('situations-travail', diagnosticId, undefined, diagnosticProgress)

  // Charger les données de situations de travail
  useEffect(() => {
    async function loadSituations() {
      try {
        const response = await fetch('/data/situations-travail.json')
        if (response.ok) {
          const data: SituationsData = await response.json()
          setSituationsData(data.data || [])
        }
      } catch (error) {
        console.error('Erreur chargement situations:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadSituations()
  }, [])

  // Écouter les changements de connexion
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Grouper les situations par métier et consolider les risques
  const groupSituations = useCallback((metierId: string): SituationGroupee[] => {
    const situationsMetier = situationsData.filter(s => s.metierId === metierId)
    const groupees = new Map<string, SituationGroupee>()

    for (const s of situationsMetier) {
      const existing = groupees.get(s.situationTravail)
      if (existing) {
        for (const risque of s.risquesAssocies) {
          if (!existing.risques.includes(risque)) {
            existing.risques.push(risque)
          }
        }
      } else {
        groupees.set(s.situationTravail, {
          situation: s.situationTravail,
          ordreAffichage: s.ordreAffichage,
          risques: [...s.risquesAssocies],
        })
      }
    }

    return Array.from(groupees.values())
      .sort((a, b) => a.ordreAffichage - b.ordreAffichage)
  }, [situationsData])

  // Calculer les situations par métier
  const situationsParMetier = useMemo(() => {
    const result: Record<string, SituationGroupee[]> = {}
    for (const metierId of metierIds) {
      result[metierId] = groupSituations(metierId)
    }
    return result
  }, [metierIds, groupSituations])

  // Navigation vers Q1
  const handleNext = () => {
    trackEvent('commencer le questionnaire')
    navigate(`/diagnostic/${diagnosticId}/q1`, {
      state: diagnosticState,
    })
  }

  // Retour au dashboard
  const handleBack = () => {
    navigate('/dashboard')
  }

  // Métier actif (pour le compteur en bas — utilise le premier par défaut)
  const firstMetier = metiersSelectionnes[0]
  const firstSituations = firstMetier ? situationsParMetier[firstMetier.code] || [] : []

  // Si pas de métiers et chargement terminé, rediriger vers dashboard
  if (metierIds.length === 0 && !isLoading && !isLoadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground mb-4">Aucun métier sélectionné.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-primary hover:underline"
            >
              Retour au tableau de bord
            </button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Affichage chargement
  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        isOnline={isOnline}
        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-3 border-primary border-t-transparent rounded-full" />
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      showNavigation={true}
      onBack={handleBack}
      backLabel="Tableau de bord"
      onNext={handleNext}
      nextLabel="Commencer le questionnaire"
      isOnline={isOnline}
      diagnosticState={diagnosticState}
    >
      <div>
        {/* En-tête */}
        <h1 className="text-2xl font-bold">
          Situations de travail à risque
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Voici les principales situations de travail identifiées comme prioritaires
          par d'autres entreprises de vos métiers. Cette information vous aide à
          mieux appréhender les risques avant de répondre au questionnaire.
        </p>

        {/* Alerte informative */}
        <Alert className="mt-6 border-blue-200 bg-blue-50 text-blue-900 [&>svg]:text-blue-600">
          <Info className="size-4" />
          <AlertTitle className="text-blue-900">Information préalable</AlertTitle>
          <AlertDescription className="text-blue-800">
            Ces situations de travail sont présentées à titre informatif.
            Elles reflètent les priorités identifiées par le secteur.
            Vous pourrez évaluer vos pratiques de prévention dans le questionnaire suivant.
          </AlertDescription>
        </Alert>

        {/* Contenu — tabs si multi-métiers, liste directe sinon */}
        {metiersSelectionnes.length > 1 ? (
          <Tabs defaultValue={metiersSelectionnes[0]?.code} className="mt-6">
            <TabsList>
              {metiersSelectionnes.map((metier, index) => (
                <TabsTrigger key={metier.code} value={metier.code} className="gap-2">
                  <Badge variant="outline" className="w-5 h-5 p-0 flex items-center justify-center rounded-full text-xs">
                    {index + 1}
                  </Badge>
                  {metier.nom}
                </TabsTrigger>
              ))}
            </TabsList>
            {metiersSelectionnes.map((metier) => (
              <TabsContent key={metier.code} value={metier.code} className="mt-4">
                <MetierSituations
                  metier={metier}
                  situations={situationsParMetier[metier.code] || []}
                />
              </TabsContent>
            ))}
          </Tabs>
        ) : firstMetier ? (
          <div className="mt-6">
            <MetierSituations
              metier={firstMetier}
              situations={firstSituations}
            />
          </div>
        ) : null}
      </div>
    </DiagnosticLayout>
  )
}
